import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import Heading from '~/components/Shared/Heading/Heading';
import { UserForm, UserItem, UserValues } from '~/components/UserForm';
import Page from '~/components/Shared/Layout/Page/Page';
import { Button, Spinner } from '~/ui';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useTranslation } from 'react-i18next';
import Tooltip from '~/components/Shared/Tooltip/Tooltip';
import styles from './EditUser.module.scss';
import { USER_ROLES } from '~/utils/getUserRole';
import { getPaginate, getParams } from '~/utils/getPaginate';
import { UserTabs } from '~/utils/types/dashboard';
import { AuthenticationContext } from '~/components/Authentication/AuthenticationProvider';
import { useContext } from 'react';

const EditUser = (): JSX.Element => {
  const { userID } = useParams();
  const navigate = useNavigate();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const client = useQueryClient();
  const { t } = useTranslation();
  const { value, paginate } = getPaginate();
  const { title, value: paramsValue } = getParams(UserTabs.ACTIVE_TRIP);
  const { user: currentUser } = useContext(AuthenticationContext);

  const {
    data: user,
    isLoading,
    isFetching,
  } = useQuery<UserItem>({
    queryFn: async () => {
      const response = await instance.get(`admin/users/${userID}`);
      return response.data.users.data;
    },
    queryKey: ['user', userID],
    onError: error => {
      toastError(error);
    },
  });

  const { mutateAsync: blockUser } = useMutation(
    async (value: boolean) => {
      const result = await instance.post(`admin/block-user/${userID}`, {
        block: value,
      });

      return result.data;
    },
    {
      onSuccess: () => {
        toastSuccess(t('status_successfully_updated'));
        client.invalidateQueries(['user', userID]);
        client.invalidateQueries(['users']);
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const { mutateAsync: updateUser } = useMutation(
    async ({
      name,
      uniqueId,
      uuid,
      patronymic,
      phone,
      email,
      password,
      business_trips,
      company_name,
      grade,
      role,
      accommodations_id,
      assets,
      timezone,
      sections,
      food_id,
      is_novice,
      section_all,
    }: UserValues) => {
      const section_ids = sections.map(item => item.id);
      const requestBody: Partial<UserValues> = {
        name,
        uniqueId,
        uuid,
        patronymic,
        phone,
        email,
        business_trips,
        company_name,
        grade_id: grade?.id,
        role_id: role.id,
        accommodations_id,
        utc: timezone.id,
        ...(section_all ? {} : { section_ids }),
        ...(section_all ? { section_all } : {}),
        is_novice,
        ...(role.name === USER_ROLES.ASSET_ADMIN
          ? { asset_id: assets.id }
          : role.name === USER_ROLES.FOOD_ADMIN
            ? { food_id }
            : { company_name }),
      };

      if (password !== user?.password) {
        requestBody.password = password;
      }

      const response = await instance.patch(
        `admin/users/${userID}`,
        requestBody
      );

      return response.data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(['users']);
        toastSuccess(t('employee_edited'));
        navigate(
          `/user${
            paginate ? `?${value}=${paginate}&${title}=${paramsValue}` : ''
          }`
        );
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  return (
    <Page
      heading={
        <Heading
          text={user?.full_name}
          badge={
            <Tooltip
              className={styles.tooltip}
              title={user?.is_blocked ? t('blocked_in') : t('not_blocked')}
            >
              {user?.is_blocked ? (
                <span className={styles.blockedBadge}>{t('blocked')}</span>
              ) : (
                <span className={styles.activeBadge}>{t('is_active')}</span>
              )}
            </Tooltip>
          }
          actions={
            userID !== currentUser?.data.id?.toString() ? (
              <Button
                disabled={false}
                text={!user?.is_blocked ? t('block') : t('unblock')}
                onClick={() => blockUser(!user?.is_blocked)}
              />
            ) : null
          }
        />
      }
    >
      {user && !isLoading ? (
        <UserForm
          isLoading={isFetching || isLoading}
          user={user}
          isEditing={true}
          onSubmit={values => updateUser(values)}
        />
      ) : (
        <Spinner />
      )}
    </Page>
  );
};

export default EditUser;
